import { LinearGradient } from 'expo-linear-gradient';
import { range } from 'lodash';
import type { SVGProps } from 'react';
import * as React from 'react';
import type { ColorValue } from 'react-native';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    borderCurve,
    CircleIcon,
    FootnoteRegular,
    Icon,
    isWeb,
    LabelRegular,
    LockIcon,
    type StackProps,
    tokens,
    View,
    XStack,
    YStack,
} from 'smoothie';

import CardChip from '../../../../assets/images/cards/card-chip.svg';
import CardTap from '../../../../assets/images/cards/card-tap.svg';
import FruitfulLogoBlack from '../../../../assets/images/logos/fruitful-logo-icon-only.svg';
import FruitfulLogoWhite from '../../../../assets/images/logos/fruitful-logo-icon-only-white.svg';
import MasterCardLogo from '../../../../assets/images/logos/mastercard-logo.svg';
import {
    emphasizeCardStatus,
    isCardLocked,
    isCardSuspended,
} from '../../../common/utils/bankingUtils';
import {
    CARD_ASPECT_RATIO,
    CardColor,
    CardForm,
    CardStatus,
    type UserCardStatusReason,
} from '../../types/Card';

const PHYSICAL_CARD_BACKGROUND_COLOR = [
    tokens.color.light.green.green1100,
    tokens.color.light.green.green1200,
] as const;

const VIRTUAL_CARD_BACKGROUND_COLOR_MAP: Record<
    CardColor,
    readonly [ColorValue, ColorValue]
> = {
    [CardColor.GREEN]: [
        tokens.color.light.wasabi.wasabi500,
        tokens.color.light.wasabi.wasabi700,
    ],
    [CardColor.PINK]: [
        tokens.color.light.orchidPink.orchidPink1000,
        tokens.color.light.orchidPink.orchidPink1200,
    ],
    [CardColor.YELLOW]: [
        tokens.color.light.marigold.marigold500,
        tokens.color.light.marigold.marigold700,
    ],
    [CardColor.PEACH]: [
        tokens.color.light.peach.peach300,
        tokens.color.light.peach.peach600,
    ],
    [CardColor.BLUE]: [
        tokens.color.light.dropletBlue.dropletBlue1100,
        tokens.color.light.dropletBlue.dropletBlue1200,
    ],
    [CardColor.VIOLET]: [
        tokens.color.light.sweetViolet.sweetViolet1100,
        tokens.color.light.sweetViolet.sweetViolet1200,
    ],
};

const VIRTUAL_CARD_TEXT_COLOR_MAP: Record<CardColor, string> = {
    [CardColor.GREEN]: tokens.color.light.brand.black,
    [CardColor.PINK]: tokens.color.light.brand.white,
    [CardColor.YELLOW]: tokens.color.light.brand.black,
    [CardColor.PEACH]: tokens.color.light.brand.black,
    [CardColor.BLUE]: tokens.color.light.brand.white,
    [CardColor.VIOLET]: tokens.color.light.brand.white,
};

const TEXT_BACKGROUND_COLOR_MAP: Record<string, string> = {
    [tokens.color.light.brand.white]: 'rgba(255, 255, 255, 0.08)',
    [tokens.color.light.brand.black]: 'rgba(0, 0, 0, 0.04)',
};

const getBackgroundColor = (
    form?: CardForm,
    color?: CardColor,
): readonly [ColorValue, ColorValue, ...ColorValue[]] => {
    if (form != null && form === CardForm.PHYSICAL) {
        return PHYSICAL_CARD_BACKGROUND_COLOR;
    }
    return VIRTUAL_CARD_BACKGROUND_COLOR_MAP[color ?? CardColor.BLUE];
};

const getGradientLocations = (): readonly [number, number, ...number[]] => {
    return [0, 0.9];
};

const getTextColor = (form?: CardForm, color?: CardColor): string => {
    return form === CardForm.PHYSICAL
        ? tokens.color.light.brand.white
        : VIRTUAL_CARD_TEXT_COLOR_MAP[color ?? CardColor.BLUE];
};

const getTextBackgroundColor = (form?: CardForm, color?: CardColor): string => {
    const textColor = getTextColor(form, color);
    return TEXT_BACKGROUND_COLOR_MAP[textColor] as string;
};

interface FruitfulCardLogoProps<T> extends SVGProps<T> {
    form?: CardForm;
    color?: CardColor;
}

const FruitfulCardLogo = <T,>({
    form,
    color,
    ...props
}: FruitfulCardLogoProps<T>) => {
    const textColor = getTextColor(form, color);
    return textColor === tokens.color.light.brand.black ? (
        <FruitfulLogoBlack {...props} />
    ) : (
        <FruitfulLogoWhite {...props} />
    );
};

const CardDots = ({ num, color }: { num: number; color: string }) => {
    return (
        <XStack gap={3}>
            {range(num).map((i) => (
                <Icon
                    icon={CircleIcon}
                    key={i}
                    size={6}
                    stringColor={color}
                    stringFill={color}
                />
            ))}
        </XStack>
    );
};

const GradientCardChip = ({
    width,
    height,
    borderRadius,
}: {
    width: number;
    height: number;
    borderRadius?: number;
}) => {
    return (
        <YStack width={width} height={height} overflow="hidden">
            <LinearGradient
                colors={['#BCBFC4', '#A9A9A9', '#DDDDDD'] as const}
                locations={[0, 0.5, 1] as const}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                    height,
                    width: '100%',
                    position: 'absolute',
                    top: 0,
                    borderRadius: borderRadius ?? 8,
                }}
                pointerEvents="none"
            />
            <View>
                <CardChip width={width} height={height} />
            </View>
        </YStack>
    );
};

const CardStatusOverlay = ({
    width,
    height,
    cardStatus,
    userCardStatusReason,
}: {
    width: number;
    height: number;
    cardStatus?: CardStatus;
    userCardStatusReason?: UserCardStatusReason;
}) => {
    const isLocked = isCardLocked({ cardStatus, userCardStatusReason });
    const isSuspended = isCardSuspended({ cardStatus, userCardStatusReason });
    const showStatusText =
        cardStatus === CardStatus.UNACTIVATED ||
        cardStatus === CardStatus.PENDING ||
        isSuspended;

    return (
        <>
            <View
                justifyContent={isWeb ? 'flex-start' : 'space-between'}
                style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.40)',
                    height,
                    width,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
            />
            {isLocked && (
                <View
                    position="absolute"
                    width={width}
                    height={height}
                    alignItems="center"
                    justifyContent="center"
                >
                    <View
                        backgroundColor="$white"
                        padding={16}
                        borderRadius={20}
                    >
                        <Icon
                            icon={LockIcon}
                            size={height * 0.2}
                            color="$black"
                        />
                    </View>
                </View>
            )}
            {showStatusText && (
                <View
                    position="absolute"
                    margin="auto"
                    top={0}
                    left={0}
                    paddingHorizontal={4}
                    width={width}
                    height={height}
                    alignItems="flex-end"
                >
                    <View
                        marginTop={6}
                        backgroundColor="$white"
                        padding={8}
                        borderRadius={20}
                    >
                        {cardStatus === CardStatus.UNACTIVATED && (
                            <BodyMediumEmphasized marginHorizontal={8}>
                                Activate card
                            </BodyMediumEmphasized>
                        )}
                        {cardStatus === CardStatus.PENDING && (
                            <BodyMediumEmphasized marginHorizontal={8}>
                                Processing
                            </BodyMediumEmphasized>
                        )}
                        {isSuspended && (
                            <BodyMediumEmphasized marginHorizontal={8}>
                                Suspended
                            </BodyMediumEmphasized>
                        )}
                    </View>
                </View>
            )}
        </>
    );
};

interface DummyFruitfulCardProps extends StackProps {
    form: CardForm;
    showFormLabel?: boolean;
    cardHolderName?: string;
    color?: CardColor;
}

export const DummyFruitfulCard = ({
    form,
    showFormLabel = true,
    cardHolderName,
    color,
    ...props
}: DummyFruitfulCardProps) => {
    const width = 218;
    const height = width / CARD_ASPECT_RATIO;
    const isVirtual = form === CardForm.VIRTUAL;

    const backgroundColor = getBackgroundColor(form, color);
    const locations = getGradientLocations();
    const textColor = getTextColor(form, color);

    return (
        <YStack
            justifyContent="space-between"
            width={width}
            height={height}
            borderRadius={12}
            paddingVertical={8}
            paddingHorizontal={10}
            overflow="hidden"
            {...borderCurve('continuous')}
            {...props}
        >
            <LinearGradient
                colors={backgroundColor}
                locations={locations}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                    height: height + 24,
                    width: width + 48,
                    position: 'absolute',
                    top: 0,
                    left: -16,
                }}
                pointerEvents="none"
            />
            <XStack justifyContent="space-between" gap={2}>
                {form === CardForm.VIRTUAL && (
                    <View alignSelf="flex-start">
                        <FruitfulCardLogo
                            form={form}
                            color={color}
                            width={28}
                            height={28}
                        />
                    </View>
                )}
                {showFormLabel && (
                    <View marginLeft="auto">
                        <LabelRegular color={textColor}>
                            {isVirtual ? 'Virtual' : 'Physical'}
                        </LabelRegular>
                    </View>
                )}
            </XStack>
            {form === CardForm.PHYSICAL && (
                <XStack
                    justifyContent="space-between"
                    marginTop={showFormLabel ? 12 : 40}
                >
                    <XStack alignItems="center" gap={12}>
                        <GradientCardChip width={36} height={32} />
                        <CardTap />
                    </XStack>
                    <FruitfulCardLogo form={form} width={28} height={28} />
                </XStack>
            )}
            <XStack justifyContent="space-between">
                <LabelRegular
                    alignSelf="flex-end"
                    marginBottom={4}
                    color={textColor}
                >
                    {cardHolderName}
                </LabelRegular>
                <XStack height={44} width={48} alignSelf="flex-end">
                    <MasterCardLogo width={54} height={54} />
                </XStack>
            </XStack>
        </YStack>
    );
};

interface FruitfulCardIconProps extends StackProps {
    form: CardForm;
    color?: CardColor;
}

export const FruitfulCardIcon = ({
    form,
    color,
    ...props
}: FruitfulCardIconProps) => {
    const backgroundColor = getBackgroundColor(form, color);
    const locations = getGradientLocations();

    return (
        <YStack
            justifyContent="space-between"
            width={48}
            height={32}
            borderRadius={4}
            padding={2}
            overflow="hidden"
            {...borderCurve('continuous')}
            {...props}
        >
            <LinearGradient
                colors={backgroundColor}
                locations={locations}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                    // Manually adjust height + width to account for padding (necessary for how LinearGradient renders in web)
                    height: 32 + 16,
                    width: 48 + 16,
                    position: 'absolute',
                    top: 0,
                    left: -16,
                }}
                pointerEvents="none"
            />
            {form === CardForm.VIRTUAL && (
                <XStack justifyContent="space-between" gap={2}>
                    <View alignSelf="flex-start">
                        <FruitfulCardLogo
                            form={form}
                            color={color}
                            width={6}
                            height={6}
                        />
                    </View>
                </XStack>
            )}
            {form === CardForm.PHYSICAL && (
                <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    marginTop="auto"
                    marginBottom={8}
                >
                    <GradientCardChip width={7} height={7} borderRadius={1} />
                    <FruitfulCardLogo
                        form={form}
                        color={color}
                        width={8}
                        height={8}
                    />
                </XStack>
            )}
            <View marginLeft="auto">
                <MasterCardLogo width={8} height={5} />
            </View>
        </YStack>
    );
};

export const FruitfulCardsGraphic = ({
    hasPhysicalCard,
    hasVirtualCard,
    virtualCardColor,
}: {
    hasPhysicalCard: boolean;
    hasVirtualCard: boolean;
    virtualCardColor: CardColor | undefined;
}) => {
    return (
        <YStack justifyContent="center" minWidth={64} alignItems="center">
            {hasPhysicalCard && hasVirtualCard ? (
                <View>
                    <View marginLeft={14} marginBottom={-20} zIndex={1}>
                        <FruitfulCardIcon form={CardForm.PHYSICAL} />
                    </View>
                    <FruitfulCardIcon
                        form={CardForm.VIRTUAL}
                        color={virtualCardColor}
                    />
                </View>
            ) : hasVirtualCard ? (
                <FruitfulCardIcon
                    form={CardForm.VIRTUAL}
                    color={virtualCardColor}
                />
            ) : (
                <FruitfulCardIcon form={CardForm.PHYSICAL} />
            )}
        </YStack>
    );
};

interface FruitfulCardSummaryProps extends StackProps {
    cardholderName: string;
    lastFour?: string;
    cardStatus?: CardStatus;
    userCardStatusReason?: UserCardStatusReason;
    form: CardForm;
    color?: CardColor;
    onPress: () => void;
    pressDisabled?: boolean;
}

export const FruitfulCardSummary = ({
    cardholderName,
    lastFour,
    form,
    color,
    cardStatus,
    userCardStatusReason,
    onPress,
    pressDisabled,
    width,
    ...props
}: FruitfulCardSummaryProps) => {
    width = (width ?? 220) as number;

    const height = width / CARD_ASPECT_RATIO;

    const backgroundColor = getBackgroundColor(form, color);
    const locations = getGradientLocations();
    const textColor = getTextColor(form, color);

    return (
        <YStack
            justifyContent="space-between"
            width={width}
            height={height}
            borderRadius={12}
            padding={12}
            overflow="hidden"
            onPress={() => {
                if (!pressDisabled) {
                    onPress();
                }
            }}
            {...(!pressDisabled && { hoverStyle: { cursor: 'pointer' } })}
            {...borderCurve('continuous')}
            {...props}
        >
            <LinearGradient
                colors={backgroundColor}
                locations={locations}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                    // Manually adjust height + width to account for padding (necessary for how LinearGradient renders in web)
                    height: height + 24,
                    width: width + 48,
                    position: 'absolute',
                    top: 0,
                    left: -16,
                }}
                pointerEvents="none"
            />
            <XStack justifyContent="space-between" gap={2}>
                {form === CardForm.VIRTUAL && (
                    <View alignSelf="flex-start">
                        <FruitfulCardLogo
                            form={form}
                            color={color}
                            width={24}
                            height={24}
                        />
                    </View>
                )}
                <View marginLeft="auto">
                    {lastFour != null ? (
                        <LabelRegular color={textColor} marginLeft={4}>
                            {form.charAt(0) + form.slice(1).toLowerCase()} *
                            {lastFour}
                        </LabelRegular>
                    ) : (
                        <CardDots num={4} color={textColor} />
                    )}
                </View>
            </XStack>
            {form === CardForm.PHYSICAL && (
                <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    marginBottom={8}
                >
                    <XStack gap={6} alignItems="center">
                        <GradientCardChip width={33} height={30} />
                        <CardTap width={16} height={20} />
                    </XStack>
                    <FruitfulCardLogo
                        form={form}
                        color={color}
                        width={24}
                        height={24}
                    />
                </XStack>
            )}
            <XStack
                justifyContent="space-between"
                overflow="hidden"
                height={30}
            >
                <LabelRegular color={textColor} alignSelf="flex-end">
                    {cardholderName}
                </LabelRegular>
                <MasterCardLogo width={36} height={36} />
            </XStack>
            {emphasizeCardStatus(cardStatus) && (
                <CardStatusOverlay
                    width={width}
                    height={height}
                    cardStatus={cardStatus}
                    userCardStatusReason={userCardStatusReason}
                />
            )}
        </YStack>
    );
};

interface FruitfulCardProps extends StackProps {
    cardholderName: string;
    width: number;
    form?: CardForm;
    color?: CardColor;
    cardStatus?: CardStatus;
    userCardStatusReason?: UserCardStatusReason;
    lastFour?: string;
}

const FruitfulCard = ({
    cardholderName,
    width,
    form,
    color,
    cardStatus,
    userCardStatusReason,
    lastFour,
    ...props
}: FruitfulCardProps) => {
    const height = width / CARD_ASPECT_RATIO;
    const isVirtual = form === CardForm.VIRTUAL;

    const backgroundColor = getBackgroundColor(form, color);
    const locations = getGradientLocations();
    const textColor = getTextColor(form, color);

    return (
        <YStack
            justifyContent="space-between"
            width={width}
            height={height}
            borderRadius={12}
            paddingVertical={14}
            paddingHorizontal={16}
            overflow="hidden"
            {...borderCurve('continuous')}
            {...props}
        >
            <LinearGradient
                colors={backgroundColor}
                locations={locations}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                    height: height + 24,
                    width: width + 48,
                    position: 'absolute',
                    top: 0,
                    left: -16,
                }}
                pointerEvents="none"
            />
            <XStack justifyContent="space-between" gap={2}>
                {form === CardForm.VIRTUAL && (
                    <View alignSelf="flex-start">
                        <FruitfulCardLogo
                            form={form}
                            color={color}
                            width={32}
                            height={32}
                        />
                    </View>
                )}
                <View marginLeft="auto">
                    {form != null ? (
                        <View
                            backgroundColor={getTextBackgroundColor(
                                form,
                                color,
                            )}
                            borderRadius={20}
                            paddingVertical={4}
                        >
                            <BodySmallRegular
                                color={textColor}
                                marginHorizontal={12}
                            >
                                {isVirtual ? 'Virtual' : 'Physical'}{' '}
                                {lastFour != null ? `*${lastFour}` : ''}
                            </BodySmallRegular>
                        </View>
                    ) : (
                        <FootnoteRegular color={textColor}>
                            Fruitful Card
                        </FootnoteRegular>
                    )}
                </View>
            </XStack>
            {form === CardForm.PHYSICAL && (
                <XStack justifyContent="space-between" marginTop={12}>
                    <XStack alignItems="center" gap={12}>
                        <GradientCardChip width={48} height={44} />
                        <CardTap />
                    </XStack>
                    <FruitfulCardLogo
                        form={form}
                        color={color}
                        width={32}
                        height={32}
                    />
                </XStack>
            )}
            <XStack
                justifyContent="space-between"
                height={50}
                overflow="hidden"
            >
                <BodyMediumRegular color={textColor} alignSelf="flex-end">
                    {cardholderName}
                </BodyMediumRegular>
                <MasterCardLogo width={60} height={60} />
            </XStack>
            {emphasizeCardStatus(cardStatus) && (
                <CardStatusOverlay
                    width={width}
                    height={height}
                    cardStatus={cardStatus}
                    userCardStatusReason={userCardStatusReason}
                />
            )}
        </YStack>
    );
};

export default FruitfulCard;
